import React, {
  createContext,
  PropsWithChildren,
  useState,
  useMemo,
  useCallback,
  FC,
  ReactNode,
} from 'react';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  PatientDTO,
} from 'dtos';
import {
  notEmptyString,
} from '@populateui/utils';
import TodosPanel from 'pages/Dashboard/components/TodosPanel';
import UpcomingAppointmentsPanel from 'pages/Dashboard/components/UpcomingAppointmentsPanel';
import StickyNotesPage from 'views/EMR/Sticky';

type Part = 'list' | 'empty';

export type ExtensionProps = {
  patient: Partial<PatientDTO>;
  className?: string;
  classes?: Partial<Record<Part, string>>;
  loaderSlot?: ReactNode;
  variant?: 'default' | 'compact';
  onClose: () => void;
};

type Extension = 'stickies' | 'todos' | 'upcomingAppointments';

const extensionsByType: Record<Extension, FC<ExtensionProps>> = {
  stickies: StickyNotesPage,
  todos: TodosPanel,
  upcomingAppointments: UpcomingAppointmentsPanel,
};

type ViewContextValue = {
  isPanel?: boolean;
  navigate: (value: string) => void;
  tabValue?: string | null;
  id: number;
  Extension: FC<ExtensionProps> | null;
  extensionExpanded?: boolean;
  extensionName?: string;
  setExtensionExpanded?: (value: Extension | null) => void;
};

type Props = PropsWithChildren & {
  initialTab?: string;
  isPanel?: boolean;
  entityId?: number;
  // Panel mode only: when `tab` is given, the tab is controlled by the caller
  tab?: string;
  onTabChange?: (value: string) => void;
};

export const ViewContext = createContext<ViewContextValue>({
  isPanel: false,
  navigate: noop,
  tabValue: null,
  id: 0,
  Extension: null,
  extensionName: undefined,
  extensionExpanded: false,
  setExtensionExpanded: () => {},
});

export default function ViewProvider({
  children,
  initialTab,
  isPanel,
  entityId = 0,
  tab,
  onTabChange,
}: Props) {
  const [tabValue, setTabValue] = useState<string | undefined>(initialTab);
  const [extension, setExtensionExpanded] = useState<Extension | null>(null);
  const { tab: paramsTabValue = initialTab, id: _id = 0 } = useParams();
  const _navigate = useNavigate();
  const id = Number(isPanel ? entityId : _id);
  const isControlled = isPanel && !isNil(tab);
  const panelTabValue = isControlled ? tab : tabValue;

  const navigate = useCallback((value: string) => {
    if (isControlled) {
      onTabChange?.(value);
    } else if (isPanel) {
      setTabValue(value);
    } else {
      _navigate(value, { replace: true });
    }
  }, [_navigate, isPanel, isControlled, onTabChange, setTabValue]);

  const value = useMemo<ViewContextValue>(() => ({
    isPanel,
    tabValue: isPanel ? panelTabValue : paramsTabValue,
    navigate,
    id,
    extensionName: extension ?? undefined,
    Extension: isNil(extension) ? null : extensionsByType[extension],
    extensionExpanded: notEmptyString(extension),
    setExtensionExpanded,
  }), [
    isPanel,
    panelTabValue,
    paramsTabValue,
    navigate,
    id,
    extension,
    setExtensionExpanded,
  ]);

  return (
    <ViewContext.Provider value={value}>
      {children}
    </ViewContext.Provider>
  );
}
